import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Upload, X, ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import ProgressTracker from '../components/ProgressTracker'
import MainLayout from '../layouts/MainLayout'
import { put } from '@vercel/blob'
import logger from '../utils/logger'
import { toast } from 'react-hot-toast'
import { PageSEO } from '../components/SEO/PageSEO'
import debug from '../utils/debug'
import useAnalytics from '../hooks/useAnalytics'
import { useEventForm } from '../context/EventFormContext'

/**
 * InspirationUpload Component
 * Allows users to upload visual inspiration for their event
 * Features:
 * - Photo upload with preview
 * - URL input with validation
 * - Data persistence in localStorage
 * - Progress tracking
 * - Responsive layout
 * - URL normalization (adds https:// if missing)
 * - Image compression to reduce file size
 */
export default function InspirationUpload() {
  const navigate = useNavigate();
  const { trackButtonClick, trackStepCompleted, trackStepStart, trackError } = useAnalytics();
  const { formData, updateFormData } = useEventForm();

  const savedInspiration = formData.inspiration || {};
  const [uploadedPhoto, setUploadedPhoto] = useState(savedInspiration.uploadedPhoto || null);
  const [inspirationLink, setInspirationLink] = useState(savedInspiration.inspirationLink || '');
  const [linkError, setLinkError] = useState('');
  const [notification, setNotification] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    trackStepStart('Inspiration Upload', {
      formStep: 4,
      hasExistingData: !!formData.inspiration,
      hasDreamDescription: !!formData.dream
    });
    debug.log('InspirationUpload', 'Form started');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Compresses and resizes image to reduce file size
  const compressImage = (file, maxWidth = 1000, quality = 0.4) => {
    return new Promise((resolve, reject) => {
      // Create an image element
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        // Create canvas
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        
        // Calculate new dimensions while maintaining aspect ratio
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress image on canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to Blob with specified quality
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.error('Canvas to Blob conversion failed');
              reject(new Error('Image compression failed'));
              return;
            }
            
            // Create a new file from the blob
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            
            // Log detailed file size information - exact bytes and formatted sizes
            logger.info('Image compressed', {
              originalSize: {
                bytes: file.size,
                kb: Math.round(file.size / 1024),
                formatted: formatFileSize(file.size)
              },
              compressedSize: {
                bytes: compressedFile.size,
                kb: Math.round(compressedFile.size / 1024),
                formatted: formatFileSize(compressedFile.size)
              },
              dimensions: {
                original: `${img.width}x${img.height}`,
                compressed: `${width}x${height}`
              },
              compressionRatio: `${((1 - compressedFile.size / file.size) * 100).toFixed(1)}%`,
              quality
            });
            
            resolve(compressedFile);
          },
          file.type,
          quality
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    });
  };
  
  // Helper function to format file size in a human-readable format
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Handles file upload to Vercel Blob storage
  const uploadToVercelBlob = async (file) => {
    try {
      // Log the exact file size being uploaded
      debug.log('InspirationUpload', `Uploading file to Vercel Blob - Size: ${file.size} bytes (${formatFileSize(file.size)})`);
      
      const filename = `inspirations/${Date.now()}-${file.name}`;
      const { url } = await put(filename, file, {
        access: 'public',
        token: process.env.REACT_APP_BLOB_READ_WRITE_TOKEN
      });

      debug.log('InspirationUpload', 'Upload successful, URL:', url);
      return url;
    } catch (error) {
      debug.error('InspirationUpload', 'Error uploading to Vercel Blob:', error);
      debug.error('InspirationUpload', 'Error details:', {
        message: error.message,
        stack: error.stack,
      });
      toast.error('Failed to upload image. Please try again.');
      return null;
    }
  };

  const handleFileUploadError = (error, fileInfo = {}) => {
    toast.error('Failed to upload file. Please try again.');
    trackError('File Upload Error', {
      reason: error.message || 'unknown_error',
      component: 'inspiration_upload',
      ...fileInfo
    });
  };

  // Handles photo upload from file input
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Size validation (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setNotification({
        message: 'Please choose a file smaller than 10MB',
        type: 'error'
      });
      setTimeout(() => setNotification(null), 3000);
      
      // Enhanced error tracking for size issues
      handleFileUploadError({ message: 'file_too_large' }, {
        fileSize: formatFileSize(file.size),
        exactSize: file.size
      });
      return;
    }

    // Log exact input file size
    logger.info('Original file selected', {
      name: file.name,
      type: file.type,
      exactSize: file.size,
      formattedSize: formatFileSize(file.size)
    });

    setIsUploading(true);
    
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadedPhoto(reader.result);
        updateFormData({
          inspiration: { ...formData.inspiration, hasPreview: true, inspirationLink }
        });
      };
      reader.readAsDataURL(file);

      const compressedFile = await compressImage(file, 1000, 0.4);
      
      const blobUrl = await uploadToVercelBlob(compressedFile);
      if (blobUrl) {
        logger.success('File uploaded', { 
          url: blobUrl,
          reduction: `${((1 - compressedFile.size / file.size) * 100).toFixed(1)}%`
        });
        
        updateFormData({
          inspiration: { ...formData.inspiration, blobUrl, inspirationLink }
        });
        
        toast.success('Photo uploaded successfully!');
        
        trackButtonClick('Upload Success', {
          type: 'inspiration_photo',
          fileType: file.type,
          compressionRatio: ((1 - compressedFile.size / file.size) * 100).toFixed(1) + '%'
        });
      }
    } catch (error) {
      debug.error('InspirationUpload', 'Error in handlePhotoUpload:', error);
      handleFileUploadError(error, {
        fileType: file?.type,
        fileSize: formatFileSize(file.size)
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Validates URL format and structure
  const isValidUrl = (string) => {
    if (!string) return true; // Empty string is valid (optional field)
    
    // Check for valid domain extension
    if (!string.match(/\.[a-z]{2,}($|\/)/i)) {
      return false;
    }
    
    // Normalize URLs by adding https:// if missing
    let urlString = string;
    if (!string.match(/^(https?:\/\/|www\.)/i)) {
      urlString = 'https://' + string;
    }
    // Add https:// to www. URLs
    if (string.match(/^www\./i)) {
      urlString = 'https://' + string;
    }

    try {
      new URL(urlString); // Attempt to construct URL object
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleLinkChange = (e) => {
    const value = e.target.value;
    setInspirationLink(value);
    updateFormData({
      inspiration: { ...formData.inspiration, inspirationLink: value }
    });
  };

  // Validates link on input blur
  const handleLinkBlur = () => {
    if (inspirationLink && !isValidUrl(inspirationLink)) {
      setLinkError('Please enter a valid website address (e.g., pinterest.com)');
    } else {
      setLinkError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    updateFormData({
      inspiration: { ...formData.inspiration, inspirationLink }
    });
    
    const hasUploaded = !!uploadedPhoto || !!inspirationLink;
    
    trackStepCompleted('Inspiration Upload', {
      hasPhoto: !!uploadedPhoto,
      hasLink: !!inspirationLink,
      step: 4,
      skipped: !hasUploaded
    });
    
    navigate('/contact');
  };

  const handleSkip = (e) => {
    e.preventDefault();
    trackButtonClick('Skip Inspiration Upload', {
      step: 4,
      had_started_upload: !!uploadedPhoto || !!inspirationLink
    });
    handleSubmit(e);
  };

  // Drag and drop event handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('border-blue-400', 'bg-blue-100');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-blue-400', 'bg-blue-100');
  };

  // Handles file drop events
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-blue-400', 'bg-blue-100');
    
    const file = e.dataTransfer.files[0];
    if (file) {
      // Size validation
      if (file.size > 10 * 1024 * 1024) {
        setNotification({
          message: 'Please choose a file smaller than 10MB',
          type: 'error'
        });
        setTimeout(() => setNotification(null), 3000);
        handleFileUploadError({ message: 'file_too_large' }, {
          method: 'drag_and_drop',
          fileSize: formatFileSize(file.size)
        });
        return;
      }
      
      // Log exact input file size for drag and drop
      logger.info('File dropped', {
        name: file.name,
        type: file.type,
        exactSize: file.size,
        formattedSize: formatFileSize(file.size)
      });
      
      setIsUploading(true);
      
      try {
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!validTypes.includes(file.type)) {
          alert('Please upload only JPEG, JPG or PNG files');
          setIsUploading(false);
          handleFileUploadError({ message: 'invalid_file_type' }, {
            method: 'drag_and_drop',
            fileType: file.type
          });
          return;
        }

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setUploadedPhoto(reader.result);
        };
        reader.readAsDataURL(file);

        // Compress the image before uploading (more aggressive settings)
        const compressedFile = await compressImage(file, 1000, 0.4);
        
        // Upload compressed file to blob storage
        const blobUrl = await uploadToVercelBlob(compressedFile);
        
        if (blobUrl) {
          updateFormData({
            inspiration: { ...formData.inspiration, blobUrl, inspirationLink }
          });
          
          logger.success('File uploaded via drag and drop', { 
            url: blobUrl,
            reduction: `${((1 - compressedFile.size / file.size) * 100).toFixed(1)}%`
          });
          
          toast.success('Photo uploaded successfully!');
          
          trackButtonClick('Upload Success', {
            type: 'inspiration_photo',
            method: 'drag_and_drop',
            fileType: file.type,
            compressionRatio: ((1 - compressedFile.size / file.size) * 100).toFixed(1) + '%'
          });
        }
      } catch (error) {
        debug.error('InspirationUpload', 'Error in handleDrop:', error);
        handleFileUploadError(error, {
          fileType: file?.type,
          fileSize: formatFileSize(file.size),
          method: 'drag_and_drop'
        });
      } finally {
        setIsUploading(false);
      }
    }
  };

  return (
    <MainLayout
      progressTracker={<ProgressTracker currentStep={4} />}
    >
      {/* Add PageSEO component with noindex */}
      <PageSEO
        title="Inspiration Upload | LetsHost"
        description="Upload inspiration for your event"
        path="/inspiration"
        noindex={true}
      />
      
      {/* Notification toast */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-4 right-4 z-50 max-w-sm"
          >
            <div className={`rounded-lg px-4 py-3 shadow-lg ${
              notification.type === 'error' 
                ? 'bg-white border-l-4 border-red-500'
                : 'bg-white border-l-4 border-blue-500'
            }`}>
              <div className="flex items-center">
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    notification.type === 'error' 
                      ? 'text-red-800' 
                      : 'text-blue-800'
                  }`}>
                    {notification.message}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main content container */}
      <motion.div 
        className="w-full mx-auto mt-5 bg-white rounded-3xl shadow-xl overflow-hidden px-4 sm:px-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Header section */}
        <div className="pt-5 pb-3 max-w-2xl mx-auto">
          <h2 className="text-2xl font-semibold text-gray-900">
            Upload a photo of what you want your event to look like
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Venues respond 40% faster when they can see your vision
          </p>
        </div>

        {/* Content container */}
        <div className="flex flex-col md:flex-row md:space-x-6 max-w-2xl mx-auto pb-5">
          {/* Upload area */}
          <div className="w-full md:w-3/5 mb-4 md:mb-0">
            <AnimatePresence mode="wait">
              {!uploadedPhoto ? (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex justify-center"
                >
                  <motion.label
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    htmlFor="photo-upload"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className="w-full p-5 flex flex-col items-center gap-3 cursor-pointer 
                      rounded-xl border-2 border-dashed border-blue-300 bg-blue-50 
                      hover:bg-blue-100 hover:border-blue-400 transition-all duration-200"
                  >
                    <span className="bg-blue-100 text-blue-600 p-3 rounded-full">
                      <Upload className="w-6 h-6" />
                    </span>
                    <div className="text-center">
                      <span className="text-blue-600 font-semibold text-base block">
                        Upload inspiration photo
                      </span>
                      <span className="text-blue-400 text-xs block mt-1">
                        JPEG, JPG or PNG (max. 10MB, will be optimized)
                      </span>
                    </div>
                    <input
                      type="file"
                      id="photo-upload"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handlePhotoUpload}
                      className="hidden"
                    />
                  </motion.label>
                </motion.div>
              ) : (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="relative"
                >
                  <div className="aspect-square max-h-[250px] relative overflow-hidden rounded-lg shadow-md">
                    <img 
                      src={uploadedPhoto} 
                      alt="Uploaded inspiration" 
                      className="w-full h-full object-cover"
                    />
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setUploadedPhoto(null)}
                      className="absolute top-2 right-2 bg-white/90 backdrop-blur text-red-500 p-2 rounded-full hover:bg-white transition-all duration-200 shadow-md"
                    >
                      <X className="w-4 h-4" />
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Link input */}
          <div className="w-full md:w-2/5">
            <div className="relative h-full flex flex-col">
              <input
                type="url"
                value={inspirationLink}
                onChange={handleLinkChange}
                onBlur={handleLinkBlur}
                placeholder="Or share a link to inspiration"
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pl-4 text-sm"
              />
              {linkError ? (
                <p className="mt-2 text-xs text-red-500">{linkError}</p>
              ) : (
                <p className="mt-2 text-xs text-gray-500">
                  Pinterest, Instagram, or venue photos that match your vision
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Submit button container */}
        <div className="flex justify-center items-center gap-4 py-4 border-t border-gray-100">
          <motion.button
            onClick={handleSubmit}
            disabled={isUploading}
            className={`flex items-center gap-2 py-2.5 px-6 rounded-full font-medium
              transition-all duration-200 shadow-md hover:shadow-lg
              ${isUploading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-emerald-400 to-emerald-600'} 
              text-white`}
          >
            {isUploading ? (
              <span>Processing...</span>
            ) : (
              <>
                Continue
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </motion.button>
          <button
            onClick={handleSkip}
            className="text-gray-500 text-sm hover:text-gray-700 transition-colors"
          >
            Skip for now
          </button>
        </div>
      </motion.div>
    </MainLayout>
  );
}
